import React, { useContext, useState, useMemo } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Collapse } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {ThemeContext} from "styled-components";
import {AuthContext} from "../../auth/providers/AuthProvider";
import {useQuery} from "react-query";
import {listUsersTemplates} from "../../../services/template/Templates";
import {GetIcon} from "../../../utils/get_icon/GetIcon";
import {CsrfContext} from "../../../providers/CsrfProvider";
import {useNavigate, useLocation} from "react-router-dom";
import IconAddScheduled from "../../../assets/Images/Icons/IconAddScheduled";
import {useGroup} from "../../../providers/GroupProvider";

const Footer = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useContext(ThemeContext);
    const { authClaims, setAuthClaims } = useContext(AuthContext);
    const {csrfToken} = useContext(CsrfContext);
    const { selectedGroupId, loading: groupsLoading, hydratedBills, billsGroupId } = useGroup();
    const [archivedExpanded, setArchivedExpanded] = useState(false);

    // Same as Sidebar: on refresh we already have bills from GET /api/groups?bills=true; only fetch templates when user switches to a different group
    const selectedMatchesHydrated = (selectedGroupId == null && (billsGroupId == null || billsGroupId === '')) ||
        (selectedGroupId && billsGroupId && String(selectedGroupId) === String(billsGroupId));

    // Only run templates query when we don't already have bills from GET /api/groups?bills=true (avoids GET /api/bills/templates on refresh).
    const { data: fetchedTemplates = [], error, status } = useQuery(
        ["templates", selectedGroupId],
        () => listUsersTemplates(csrfToken, selectedGroupId ?? null),
        {
            enabled: !!authClaims && !!csrfToken && !groupsLoading && !selectedMatchesHydrated,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
        }
    );
    const data = selectedMatchesHydrated && Array.isArray(hydratedBills) ? hydratedBills : fetchedTemplates;
    
    // Get current template ID from URL (UUID format)
    const currentTemplateId = useMemo(() => {
        // Match UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
        const match = location.pathname.match(/\/bills\/templates\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
        const templateId = match ? match[1] : null;
        return templateId;
    }, [location.pathname]);

    // Helper function to get favorite status
    const getFavoriteStatus = (bill) => {
        // If user is the owner, always use bill-level favorite
        if (bill.owner_sub && authClaims?.user_sub && 
            bill.owner_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
            return bill.favorite || false;
        }
        
        // If user is a participant (but not owner), use their participant favorite status
        if (bill.participants && bill.participants.length > 0 && authClaims?.user_sub) {
            const userParticipant = bill.participants.find(
                p => p.user_sub && p.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()
            );
            if (userParticipant && userParticipant.favorite !== undefined) {
                return userParticipant.favorite;
            }
        }
        
        return false;
    };

    // Split bills into archived and non-archived, matching Sidebar logic (defensive: always treat as array, skip null entries)
    const { activeBills, archivedBills, hasArchived } = useMemo(() => {
        const list = Array.isArray(data) ? data.filter(Boolean) : [];
        if (list.length === 0) {
            return { activeBills: [], archivedBills: [], hasArchived: false };
        }
        
        const active = [];
        const archived = [];
        
        list.forEach(bill => {
            const isArchived = bill.archived_at != null && bill.archived_at !== '';
            if (isArchived) {
                archived.push(bill);
            } else {
                active.push(bill);
            }
        });
        
        // Sort archived bills by archived_at (most recent first)
        archived.sort((a, b) => {
            const dateA = a.archived_at ? new Date(a.archived_at) : new Date(0);
            const dateB = b.archived_at ? new Date(b.archived_at) : new Date(0);
            return dateB - dateA;
        });
        
        return {
            activeBills: active,
            archivedBills: archived,
            hasArchived: archived.length > 0
        };
    }, [data]);

    const footerStyle = {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%', // Ensure full viewport width
        margin: 0,
        padding: 0,
        backgroundColor: theme.colors.primary,
    };

    const listStyle = {
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        overflowY: 'hidden',
        whiteSpace: 'nowrap', // Prevents items from wrapping
        msOverflowStyle: 'none', /* IE and Edge */
        scrollbarWidth: 'none', /* Firefox */
        '&::-webkit-scrollbar': {
            display: 'none', /* Chrome, Safari, and Opera */
        },
        padding: '0px',
        margin: '0px',
        background: theme.colors.primary,
    };

    const listItemStyle = {
        display: 'inline-flex', // Ensure items are in a line
        marginRight: '20px', // Space between items
    };

    if (!authClaims) {
        return (<>
            <Box sx={footerStyle}/>
        </>)
    }


    return (
        <>
        <Box sx={footerStyle}>
            <List sx={listStyle}>
                {/* Add recurring bill button */}
                <ListItem disablePadding sx={listItemStyle}>
                    <ListItemButton onClick={() => navigate('/bills/templates')}>
                        <ListItemIcon  sx={{ color: theme.colors.text }}>
                            <IconAddScheduled size={28} />
                        </ListItemIcon>
                        <ListItemText primary={"Add recurring bill"} sx={{ color: theme.colors.text }} />
                    </ListItemButton>
                </ListItem>
                
                {/* Active bills - sorted by favorite first, then by created_at (newest first) */}
                {activeBills.sort((a, b) => {
                    const favoriteA = getFavoriteStatus(a);
                    const favoriteB = getFavoriteStatus(b);
                    if (favoriteA !== favoriteB) {
                        return favoriteA ? -1 : 1;
                    }
                    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                    return dateB - dateA;
                }).map((bill) => {
                    const {name, id} = bill;
                    const isActive = String(id) === String(currentTemplateId);
                    return (
                        <ListItem key={id} disablePadding sx={listItemStyle}>
                            <ListItemButton
                                onClick={ ()=>navigate(`/bills/templates/${id}`)}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: 'initial',
                                    px: 2.5,
                                    transform: isActive ? 'translateX(8px)' : 'none',
                                    transition: 'transform 200ms ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    },
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: 3,
                                        justifyContent: 'center',
                                        color: isActive ? theme.colors.secondary : theme.colors.text,
                                    }}
                                >
                                    <GetIcon
                                        billTemplateName={name}
                                        style={{
                                            color: isActive ? theme.colors.secondary : theme.colors.text, 
                                            fontSize:'28px'
                                        }}
                                    />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={name} 
                                    sx={{ 
                                        color: isActive ? theme.colors.secondary : theme.colors.text,
                                        '& .MuiTypography-root': {
                                            transition: 'color 200ms ease',
                                        },
                                    }} 
                                />
                            </ListItemButton>
                            <Divider orientation="vertical" sx={{backgroundColor: theme.colors.secondary, boxShadow: "2"}}/>
                        </ListItem>
                    );
                })}
                
                {/* Archived section */}
                {hasArchived && (
                    <>
                        <ListItem disablePadding sx={listItemStyle}>
                            <ListItemButton
                                onClick={() => setArchivedExpanded(!archivedExpanded)}
                                sx={{
                                    minHeight: 48,
                                    justifyContent: 'initial',
                                    px: 2.5,
                                }}
                            >
                                <ListItemIcon
                                    sx={{
                                        minWidth: 0,
                                        mr: archivedExpanded ? 0 : 3,
                                        justifyContent: 'center',
                                        color: theme.colors.text
                                    }}
                                >
                                    {archivedExpanded ? (
                                        <ChevronLeftIcon sx={{ fontSize: '28px' }} />
                                    ) : (
                                        <ChevronRightIcon sx={{ fontSize: '28px' }} />
                                    )}
                                </ListItemIcon>
                                {!archivedExpanded && (
                                    <ListItemText primary="Archived" sx={{ color: theme.colors.text }} />
                                )}
                            </ListItemButton>
                            <Divider orientation="vertical" sx={{backgroundColor: theme.colors.secondary, boxShadow: "2"}}/>
                        </ListItem>
                        <Collapse in={archivedExpanded} timeout="auto" unmountOnExit>
                            <List sx={{ ...listStyle, flexDirection: 'row' }}>
                                {archivedBills.map((bill) => {
                                    const {name, id} = bill;
                                    const isActive = String(id) === String(currentTemplateId);
                                    return (
                                        <ListItem key={id} disablePadding sx={listItemStyle}>
                                            <ListItemButton
                                                onClick={ ()=>navigate(`/bills/templates/${id}`)}
                                                sx={{
                                                    minHeight: 48,
                                                    justifyContent: 'initial',
                                                    px: 2.5,
                                                    transform: isActive ? 'translateX(8px)' : 'none',
                                                    transition: 'transform 200ms ease',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                                    },
                                                }}
                                            >
                                                <ListItemIcon
                                                    sx={{
                                                        minWidth: 0,
                                                        mr: 3,
                                                        justifyContent: 'center',
                                                        color: isActive ? theme.colors.secondary : theme.colors.text,
                                                    }}
                                                >
                                                    <GetIcon
                                                        billTemplateName={name}
                                                        style={{
                                                            color: isActive ? theme.colors.secondary : theme.colors.text, 
                                                            fontSize:'28px'
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={name} 
                                                    sx={{ 
                                                        color: isActive ? theme.colors.secondary : theme.colors.text,
                                                        '& .MuiTypography-root': {
                                                            transition: 'color 200ms ease',
                                                        },
                                                    }} 
                                                />
                                            </ListItemButton>
                                            <Divider orientation="vertical" sx={{backgroundColor: theme.colors.secondary, boxShadow: "2"}}/>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Collapse>
                    </>
                )}
            </List>
        </Box>
        </>
    );
};

export default Footer;
